import { dataLayerConfig } from '@aspendental/shared-components-web';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import ScheduleQuestionCheckbox from './ScheduleQuestionCheckbox';
import ScheduleQuestionRadioGroup from './ScheduleQuestionRadioGroup';
import useScheduleQuestionsStore from '@src/store/schedule-questions.store';
type ScheduleQuestionRef = {
  focus: () => void;
  getElement: () => HTMLDivElement | null;
};
const ScheduleQuestion = forwardRef<ScheduleQuestionRef, any>(({
  id,
  question_text,
  required,
  question_type,
  options,
  short_name,
  unanswered
}, ref) => {
  const questionRef = useRef<HTMLDivElement>(null);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (questionRef.current) {
        // Find the h4 element (question title) in this question
        const h4Element = questionRef.current.querySelector('h4');
        if (h4Element instanceof HTMLElement) {
          // Make h4 focusable temporarily and focus it
          h4Element.setAttribute('tabindex', '-1');
          h4Element.focus();

          // Get the h4's position and scroll with offset
          const rect = h4Element.getBoundingClientRect();
          const offsetTop = window.scrollY + rect.top - 100; // 100px offset from top

          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth'
          });

          // Remove tabindex after a short delay to clean up
          setTimeout(() => {
            h4Element.removeAttribute('tabindex');
          }, 1000);
        }
      }
    },
    getElement: () => questionRef.current
  }));
  const answers = useScheduleQuestionsStore(useShallow(s => s.questionsAnswers?.[short_name]));
  const setQuestionAnswer = useScheduleQuestionsStore(store => store.setQuestionAnswer);
  const {
    t
  } = useTranslation('index');

  // Determine if question is actually unanswered based on current answers
  const isActuallyUnanswered = useMemo(() => {
    const isRequired = required === 1 || required === '1';
    if (!isRequired) return false;
    if (question_type === 'checkbox') {
      return !answers || answers.length === 0;
    } else if (question_type === 'radio') {
      return !answers || (Array.isArray(answers) ? answers.length === 0 : answers === '');
    }
    return !answers;
  }, [answers, required, question_type]);

  // Use the computed unanswered state instead of the prop if answers exist
  const showError = answers ? isActuallyUnanswered : unanswered;

  // Handle Checkbox answer selections
  const handleCheckboxChange = (optionShortName: string) => {
    let newAnswers = [...(answers || [])];
    if (optionShortName === 'none') {
      if (newAnswers.includes('none')) {
        newAnswers = [];
      } else {
        newAnswers = ['none'];
      }
    } else {
      if (newAnswers.includes(optionShortName)) {
        newAnswers = newAnswers.filter(answer => answer !== optionShortName);
      } else {
        newAnswers.push(optionShortName);
      }
      newAnswers = newAnswers.filter(answer => answer !== 'none');
    }
    let event = '';
    if (short_name === 'current_credit_response') {
      event = 'scheduleform_step1_creditresponse';
    }
    if (event) {
      dataLayerConfig({
        event,
        selections: newAnswers.join(', ')
      });
    }
    setQuestionAnswer(short_name, newAnswers);
  };

  // Handle Radio answer selections
  const handleRadioChange = (optionsShortName: string) => {
    setQuestionAnswer(short_name, optionsShortName);
    dataLayerConfig({
      event: `scheduleform_step1_${short_name}`,
      optionsShortName
    });
  };
  return <Box key={id} ref={questionRef} data-test-id="section_schedule_question" data-question={short_name} sx={{
    mb: {
      md: 2
    }
  }}>
				<Box sx={{
      display: 'flex',
      flexDirection: {
        md: 'row',
        xs: 'column'
      },
      alignItems: 'flex-start'
    }} aria-labelledby={short_name} aria-invalid={showError} aria-describedby={showError ? `${short_name}-error` : undefined}>
					<Box sx={{
        flex: {
          md: '0 0 40%',
          xs: '1'
        }
      }}>
						{/* Enhanced error announcement for screen readers */}
						<Typography variant="header4" color={showError ? 'error.dark' : 'text.primary'} id={short_name} mb={1} sx={{
          transition: 'color 0.3s ease-in-out'
        }} aria-required={required === 1 || required === '1'}>
							{question_text}
							{(required === 1 || required === '1') && ' *'}
						</Typography>

						{/* Question Subtext */}
						{question_type === 'checkbox' && <Typography variant="bodyLargeBook" color={showError ? 'error.dark' : 'text.primary'} display="flex" width="80%" sx={{
          paddingBottom: {
            xs: '16px',
            md: '0px'
          },
          transition: 'color 0.3s ease-in-out'
        }}>
								{t('selectFromGroup')}
							</Typography>}

						{question_type === 'radio' && <Typography variant="bodyLargeBook" color={showError ? 'error.dark' : 'text.primary'} display="flex" width="80%" sx={{
          paddingBottom: {
            xs: '16px',
            md: '0px'
          },
          transition: 'color 0.3s ease-in-out'
        }}>
								{t('selectOne')}
							</Typography>}
					</Box>

					{/* Right Section: Answer Options */}
					<Box sx={{
        flex: {
          md: '0 0 40%',
          xs: '1'
        },
        display: 'flex',
        flexDirection: 'column',
        marginTop: {
          md: -2,
          xs: 0
        },
        marginLeft: {
          md: 2
        },
        // Make answer option labels red in error state
        '& .MuiFormControlLabel-root': showError ? {
          '& .MuiFormControlLabel-label': {
            color: 'error.dark !important',
            transition: 'color 0.3s ease-in-out'
          }
        } : {
          '& .MuiFormControlLabel-label': {
            transition: 'color 0.3s ease-in-out'
          }
        },
        // Also target any Typography components in answers
        '& .MuiTypography-root': showError ? {
          color: 'error.dark',
          transition: 'color 0.3s ease-in-out'
        } : {
          transition: 'color 0.3s ease-in-out'
        },
        // Target radio and checkbox components specifically
        '& .MuiRadio-root': showError ? {
          color: 'error.dark'
        } : {},
        '& .MuiCheckbox-root': showError ? {
          color: 'error.dark'
        } : {}
      }} aria-labelledby={short_name}>
						{/* Render Checkbox Group */}
						{question_type === 'checkbox' && <ScheduleQuestionCheckbox options={options} answers={answers} short_name={short_name} handleChange={handleCheckboxChange} />}
						{/* Render Radio Group */}
						{question_type === 'radio' && <ScheduleQuestionRadioGroup options={options} answer={answers} short_name={short_name} handleChange={handleRadioChange} />}
					</Box>
				</Box>
			</Box>;
});
ScheduleQuestion.displayName = 'ScheduleQuestion';
export default ScheduleQuestion;